import type { DataCenterSource } from "../types";

/**
 * Order of data center sources
 * When paginating or deserializing, sources are fetched in this order
 */
export const SOURCE_ORDER: readonly DataCenterSource[] = [
  "sdt",
  "Orquestador",
  "Operador",
] as const;

/**
 * Get the next source in the sequence
 * @param current - The current source
 * @returns The next source, or null if current is the last one
 *
 * @example
 * getNextSource("sdt") // returns "CF"
 * getNextSource("Operador") // returns null
 */
export function getNextSource(
  current: DataCenterSource,
): DataCenterSource | null {
  const currentIndex = SOURCE_ORDER.indexOf(current);

  // Source not found or is the last one
  if (currentIndex === -1 || currentIndex === SOURCE_ORDER.length - 1) {
    return null;
  }

  return SOURCE_ORDER[currentIndex + 1];
}

/**
 * Get all sources up to and including the target source
 * Used for deserialization to determine which sources need to be loaded
 *
 * @param target - The target source to reach
 * @returns Array of sources from the start up to and including target
 *
 * @example
 * getSourcesUpTo("sdt") // returns ["sdt"]
 * getSourcesUpTo("CF") // returns ["sdt", "CF"]
 * getSourcesUpTo("Orquestador") // returns ["sdt", "CF", "Orquestador"]
 * getSourcesUpTo("invalid") // returns ["sdt"] (default fallback)
 */
export function getSourcesUpTo(target: DataCenterSource): DataCenterSource[] {
  const targetIndex = SOURCE_ORDER.indexOf(target);

  // Target not found - return first source as fallback
  if (targetIndex === -1) {
    console.warn(
      `[DataCenter] Invalid target source "${target}", defaulting to first source`,
    );
    return [SOURCE_ORDER[0]];
  }

  // Return slice from start to target (inclusive)
  return SOURCE_ORDER.slice(0, targetIndex + 1) as DataCenterSource[];
}

/**
 * Get the index of a source in the order
 * @param source - The source to find
 * @returns The index (0-based), or -1 if not found
 */
export function getSourceIndex(source: DataCenterSource): number {
  return SOURCE_ORDER.indexOf(source);
}

/**
 * Check if a source is the last one in the order
 * @param source - The source to check
 * @returns True if this is the last source
 */
export function isLastSource(source: DataCenterSource): boolean {
  return getSourceIndex(source) === SOURCE_ORDER.length - 1;
}

/**
 * Get remaining sources after the current one
 * @param current - The current source
 * @returns Array of sources that come after current
 *
 * @example
 * getRemainingSources("sdt") // returns ["CF", "Orquestador", "Operador"]
 * getRemainingSources("CF") // returns ["Orquestador", "Operador"]
 * getRemainingSources("Operador") // returns []
 */
export function getRemainingSources(
  current: DataCenterSource,
): DataCenterSource[] {
  const currentIndex = getSourceIndex(current);

  if (currentIndex === -1 || currentIndex === SOURCE_ORDER.length - 1) {
    return [];
  }

  return SOURCE_ORDER.slice(currentIndex + 1) as DataCenterSource[];
}
